<template>
  <CustomModal
    :is-modal-open="isOpen"
    @toggleModal="resetModal"
    @onClickAway="resetModal"
    :overlay="'dark'"
    :with-header="true"
    :with-footer="true"
    custom-classes="tw:h-screen tw:md:h-auto tw:w-[100vw] tw:md:max-w-[588px] tw:md:w-[588px] tw:md:rounded-md tw:rounded-none"
    dataTestId="cancellation-modal"
  >
    <template #modalHeader>
      <div class="tw:flex tw:items-center">
        <h3 class="tw:font-bold tw:text-xl tw:text-gray-800">{{ $t(titleLabel) }}</h3>
      </div>
    </template>

    <div class="tw:flex tw:flex-col tw:w-full">
      <CancellationStep1
        v-if="cancellationStep === 0"
        :accommodationId="accommodationId"
        data-testid="cancellation-step-1"
      />
      <CancellationStep2
        v-if="cancellationStep === 1"
        :reasons="reasons"
        :selectedReasonObject="selectedReasonObj"
        :selectedReason="selectedReason"
        :showHint="hintsVisible"
        :accommodationId="accommodationId"
        @updateOtherExplanation="updateOtherExplanation"
        @selectReason="selectReason"
        data-testid="cancellation-step-2"
      />
      <CancellationStep3
        v-if="cancellationStep === 2"
        :accommodationId="accommodationId"
        :endDate="activeUntilDate"
        data-testid="cancellation-step-3"
      />
    </div>
    <template #modalFooter>
      <div class="tw:flex tw:flex-col tw:sm:flex-row tw:justify-end tw:w-full tw:gap-6">
        <GhostButton @click="keepBenefits" :disabled="isCancelStudioPlusLoading" data-testid="keep-benefits-button">{{
          $t(cancelBtnLabel)
        }}</GhostButton>
        <MainButton data-testid="continue-cancellation-button" @click="goToCancellation" v-if="cancellationStep === 0">
          {{ $t('cancel_flow_cta') }}
        </MainButton>
        <DangerButton
          data-testid="confirm-cancel-button"
          :disabled="selectedReason === null"
          @click="cancelPlus"
          v-if="cancellationStep === 1"
        >
          <span v-show="!isCancelStudioPlusLoading">{{ $t('subscription_cancel_confirm_cta') }}</span>
          <span v-show="isCancelStudioPlusLoading" class="tw:px-4"><SmallDotLoader color="#fff" /></span>
        </DangerButton>
      </div>
    </template>
  </CustomModal>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'

import CustomModal from '@/components/base/CustomModal.vue'
import CancellationStep1 from '@/components/billing/premiumProducts/cancellation/CancellationStep1.vue'
import CancellationStep2 from '@/components/billing/premiumProducts/cancellation/CancellationStep2.vue'
import CancellationStep3 from '@/components/billing/premiumProducts/cancellation/CancellationStep3.vue'
import GhostButton from '@/components/base/buttons/GhostButton.vue'
import MainButton from '@/components/base/buttons/MainButton.vue'
import SmallDotLoader from '@/components/base/loaders/SmallDotLoader.vue'
import DangerButton from '@/components/base/buttons/DangerButton.vue'

import appAnalytics from '@/utils/tracking'

import { useCancelStudioPlusMutation } from '@/components/payment/queries'
import { useUserDetails } from '@/layouts/queries'
import { useSessionValues } from '@/composables/useSessionValues'

const emit = defineEmits(['closeModal'])

const props = defineProps({
  isOpen: Boolean,
  accommodationName: String,
  accommodationId: Number,
})

const queryClient = useQueryClient()
const selectedReason = ref(null)
const selectedReasonObj = ref(null)
const hintsVisible = ref(false)
const otherExplanation = ref('')
const cancellationStep = ref(0)
const activeUntilDate = ref(null)
const reasons = [
  {
    group: { value: 'Business operations', label: 'sub_cancel_businesss_ops' },
    options: [
      {
        id: 'offseason',
        group: 'Business operations',
        value: "I don't need the subscription in the off-season",
        label: 'sub_cancel_ops_1',
      },
      {
        id: 'change_of_ownership',
        group: 'Business operations',
        value: "There's a change of ownership",
        label: 'tbs_plus_cancel_reason_4',
      },
      {
        id: 'using_another_partner',
        group: 'Business operations',
        value: "I'm using another product by another partner",
        label: 'sub_cancel_ops_3',
      },
    ],
  },
  {
    group: { value: 'Cost and value', label: 'sub_cancel_cost_value' },
    options: [
      {
        id: 'no_roi',
        group: 'Cost and value',
        value: 'I don’t see a return on investment',
        label: 'sub_cancel_cost_1',
      },
      {
        id: 'too_expensive',
        group: 'Cost and value',
        value: 'The subscription is too expensive',
        label: 'sub_cancel_cost_2',
      },
      {
        id: 'found_cheaper',
        group: 'Cost and value',
        value: 'I found a more cost-effective product',
        label: 'sub_cancel_cost_3',
      },
      {
        id: 'budget_constraints',
        group: 'Cost and value',
        value: 'I have budget constraints this year',
        label: 'sub_cancel_cost_4',
      },
    ],
  },
  {
    group: { value: 'Clarity and satisfaction', label: 'sub_cancel_clarity_satisfaction' },
    options: [
      {
        id: 'dont_understand_features',
        group: 'Clarity and satisfaction',
        value: 'I don’t understand how to use the features',
        label: 'sub_cancel_clarity_1',
      },
      {
        id: 'dont_understand_insights',
        group: 'Clarity and satisfaction',
        value: 'I don’t fully understand the competitor and traveler insights',
        label: 'sub_cancel_clarity_2',
      },
      {
        id: 'no_support',
        group: 'Clarity and satisfaction',
        value: 'I haven’t received enough support with the product',
        label: 'sub_cancel_clarity_3',
      },
      {
        id: 'doesnt_meet_needs',
        group: 'Clarity and satisfaction',
        value: 'The features don’t meet my property’s needs',
        label: 'sub_cancel_clarity_4',
      },
      {
        id: 'too_complex',
        group: 'Clarity and satisfaction',
        value: 'Business Studio+ feels too complex and/or time-consuming',
        label: 'sub_cancel_clarity_5',
      },
    ],
  },
  {
    group: { value: 'Other reasons', label: 'sub_cancel_other' },
    options: [
      {
        id: 'property_closed',
        group: 'Other reasons',
        value: 'My property has permanently closed',
        label: 'sub_cancel_prop_closed',
      },
      {
        id: 'other',
        group: 'Other reasons',
        value: 'Other',
        label: 'tbs_plus_cancel_reason_5',
      },
    ],
  },
]

const { cancelStudioPlus, isCancelStudioPlusLoading } = useCancelStudioPlusMutation()
const { language, userId } = useSessionValues()
const { userDetails } = useUserDetails(userId)

const cancelBtnLabel = computed(() => {
  if (cancellationStep.value === 0) return 'keep_benefits_cta'
  if (cancellationStep.value === 1) return 'keep_subscription_cta'
  if (cancellationStep.value === 2) return 'close'
  else return ''
})

const titleLabel = computed(() => {
  if (cancellationStep.value === 0) return 'subscription_cancel_header'
  if (cancellationStep.value === 1) return 'confirm_cancellation_header'
  if (cancellationStep.value === 2) return 'subscription_status_canceled'
  else return ''
})

const selectReason = reason => {
  hintsVisible.value = true
  const reasonGroup = reason.split('-')[0].trim()
  const reasonId = reason.split('-')[1].trim()
  selectedReason.value = reason
  reasons.forEach(reason => {
    if (reason.group.value === reasonGroup) {
      selectedReasonObj.value = reason.options.find(item => item.id === reasonId)
    }
  })
}

const updateOtherExplanation = text => {
  otherExplanation.value = text
}

watch(
  () => props.isOpen,
  isOpen => {
    if (isOpen) {
      appAnalytics.track(appAnalytics.events.SP_PLUS_CHURN_INTRO_SHOWN, {
        item_id: props.accommodationId,
      })
    }
  },
  { immediate: true }
)

const cancelPlus = () => {
  appAnalytics.track(appAnalytics.events.SP_CONTINUE_CANCELLATION, {
    item_id: props.accommodationId,
    reason_id: selectedReasonObj.value?.id,
  })
  cancelStudioPlus(
    {
      accommodationId: props.accommodationId,
      itemName: props.accommodationName,
      email: userDetails.value?.email,
      reason: `${selectedReasonObj.value?.value}: ${otherExplanation?.value}`,
      language: language.value,
      studioUserId: userDetails.value?.id,
      firstName: userDetails.value?.firstName,
      lastName: userDetails.value?.lastName,
    },
    {
      onSuccess: data => {
        queryClient.invalidateQueries({ queryKey: ['studioPlusCampaign'] })
        queryClient.invalidateQueries({ queryKey: ['premiumProductsData'] })
        cancellationStep.value = 2
        activeUntilDate.value = data.data.endDate
        appAnalytics.track(appAnalytics.events.SP_SUBSCRIPTION_CANCELED, {
          item_id: props.accommodationId,
          reason: `${selectedReasonObj.value.value}: ${otherExplanation.value}`,
        })
      },
      onError: () => {
        cancellationStep.value = 1
      },
    }
  )
}

const goToCancellation = () => {
  if (cancellationStep?.value === 0) {
    appAnalytics.track(appAnalytics.events.SP_PLUS_CHURN_INTRO_CONTINUE_CLICKED, {
      item_id: props.accommodationId,
    })
    cancellationStep.value = 1
  }
}

const keepBenefits = () => {
  if (cancellationStep.value === 0) {
    appAnalytics.track(appAnalytics.events.SP_PLUS_CHURN_INTRO_KEEP_BENEFITS_CLICKED, {
      item_id: props.accommodationId,
      step: 1,
    })
  } else if (cancellationStep.value === 1) {
    appAnalytics.track(appAnalytics.events.SP_PLUS_CHURN_INTRO_KEEP_BENEFITS_CLICKED, {
      item_id: props.accommodationId,
      reason_code: selectedReasonObj.value?.id,
      step: 2,
    })
  }
  resetModal()
}

const resetModal = () => {
  if (cancellationStep.value === 2) {
    queryClient.invalidateQueries({ queryKey: ['studioPlusCampaign'] })
    queryClient.invalidateQueries({ queryKey: ['premiumProductsData'] })
  }

  selectedReason.value = null
  selectedReasonObj.value = ''
  hintsVisible.value = false
  otherExplanation.value = ''
  cancellationStep.value = 0
  activeUntilDate.value = null
  emit('closeModal')
}
</script>
